import { ErrorType } from "~/enums/ErrorType";
import { LookupFieldType } from "~/enums/lookupFieldType";
import { minMaxType } from "~/enums/minMaxValidatorType";
import { ValidationReferenceType } from "~/enums/validationReferenceType";
import { ValidationModalSection } from "~/models/navigableValidations/validationSections";
import FieldPresenceValidator from "~/models/navigableValidators/fieldPresenceValidator";
import NavigableValidator from "~/models/navigableValidators/navigableValidator";
import FormElement from "../../../formability/form_builder/models/formElements/formElement";
import { FormElementType } from "../../enums/formElementType";
import { InputFormatType } from "../../../formability/form_builder/models/inputFormatType";
import EmbeddedCollectionValidator from "../navigableValidators/embeddedCollectionValidator";
import MinMaxFieldValidator from "../navigableValidators/minMaxFieldValidator";
import NavigableValidation from "./navigableValidation";
import { ValidationErrorLevel } from '~/models/validationError';

export class FormElementValidation<T> extends NavigableValidation<FormElement> {
  _validators: NavigableValidator<FormElement>[] = [
    new EmbeddedCollectionValidator({
      validatorKey: 'form_elements',
      section: ValidationModalSection.Layout
    }, this.model),
    new MinMaxFieldValidator({
      validatorKey: "min_repeatable_instances",
      section: ValidationModalSection.Layout,
      min_field: "min_repeatable_instances",
      max_field: "max_repeatable_instances",
      minMaxType:  minMaxType.numeric,
      conditional_field: "is_repeatable"
    }),
    new FieldPresenceValidator({
      validatorKey: 'object_model_search_result_label_property_ids',
      section: ValidationModalSection.Layout,
      displayName: FormElement.SETTINGS_METADATA.object_model_search_result_label_property_ids.label,
      // targetElementId: FormElement.SETTINGS_METADATA.object_model_search_result_label_property_ids.elementId, // not working as is
      conditional: (formElement: FormElement) => {
        return (formElement.element_type === FormElementType.lookupField) &&
          (formElement.lookup_type === LookupFieldType.objectModels);
      },
    }),
  ];

  get validators() {
    this.addCharacterCountMinValidator();
    this.addCheckboxRangeMinValidator();
    this.addMinValueValidator();
    this.addValidatorElementValidators();
    this.addImageAccessibilityValidator();

    return this._validators;
  }

  addCharacterCountMinValidator(): void {
    if (![FormElementType.textField, FormElementType.textArea].includes(this.model["element_type"])) return;
    const validatorKey = 'character_count_min';
    if (this.hasValidatorFor(validatorKey)) return;

    this._validators.push(new MinMaxFieldValidator({
      validatorKey,
      section: ValidationModalSection.Layout,
      min_field: validatorKey,
      max_field: "character_count_max",
      minMaxType: minMaxType.numeric,
    }));
  }

  addCheckboxRangeMinValidator(): void {
    if (this.model["element_type"] !== FormElementType.checkboxGroup) return;
    const validatorKey = 'checkbox_range_min';
    if (this.hasValidatorFor(validatorKey)) return;

    this._validators.push(new MinMaxFieldValidator({
      validatorKey,
      section: ValidationModalSection.Layout,
      min_field: validatorKey,
      max_field: "checkbox_range_max",
      minMaxType: minMaxType.numeric,
      conditional_field: "is_required",
    }));
  }

  addMinValueValidator(): void {
    if (this.model["element_type"] !== FormElementType.textField ||
        ![InputFormatType.Numeric, InputFormatType.Currency].includes(this.model['format_type'])) {
      return;
    }
    const validatorKey = 'min_value';
    if (this.hasValidatorFor(validatorKey)) return;

    this._validators.push(new MinMaxFieldValidator({
      validatorKey: validatorKey,
      section: ValidationModalSection.Layout,
      min_field: validatorKey,
      max_field: "max_value",
      minMaxType: minMaxType.numeric,
    }));
  }

  addValidatorElementValidators(): void {
    if (this.model["element_type"] !== FormElementType.validator) return;

    const labelTitleValidatorKey = 'label_title';
    if (!this.hasValidatorFor(labelTitleValidatorKey)) {
      this._validators.push(new FieldPresenceValidator({
        validatorKey: labelTitleValidatorKey,
        section: ValidationModalSection.Layout,
        displayName: "Validation Message",
        message: "You must add a custom validation message to your Validator before you can Publish",
        // targetElementId: FormElement.SETTINGS_METADATA.object_model_search_result_label_property_ids.elementId, // not working as is
      }));
    }

    const validationRuleSetsValidatorKey = 'validation_rule_conditional_rule_sets';
    if (!this.hasValidatorFor(validationRuleSetsValidatorKey)) {
      this._validators.push(new NavigableValidator({
        validatorKey: validationRuleSetsValidatorKey,
        displayName: "Validation Rule",
        section: ValidationModalSection.Layout,
        validator: (value, model) => {
          return value && Object.keys(value).length > 0
        },
        message: "You must configure a validation rule before you can Publish",
        type: ErrorType.mustBePresent,
        conditional: (formElement: FormElement) => {
          return (formElement.validation_reference_type === ValidationReferenceType.form
          )
        },
      }));
    }

    const lookupValidationRuleSetsValidatorKey = 'lookup_validation_rule_conditional_rule_sets';
    if (!this.hasValidatorFor(lookupValidationRuleSetsValidatorKey)) {
      this._validators.push(new NavigableValidator({
        validatorKey: lookupValidationRuleSetsValidatorKey,
        displayName: "Validation Rule",
        section: ValidationModalSection.Layout,
        validator: (value, model) => {
          return value && Object.keys(value).length > 0
        },
        message: "You must configure a validation rule before you can Publish",
        type: ErrorType.mustBePresent,
        conditional: (formElement: FormElement) => {
          return (formElement.validation_reference_type === ValidationReferenceType.objectModel
          )
        },
      }));
    }
  }

  addImageAccessibilityValidator(): void {
    if (this.model['element_type'] !== FormElementType.paragraph) return;
    const validatorKey = 'label_title';
    if (this.hasValidatorFor(validatorKey)) return;

    this._validators.push(new NavigableValidator({
      validatorKey: 'label_title',
      displayName: 'Paragraph Element',
      message: '', // Explicitly empty to prevent tooltip
      section: ValidationModalSection.Layout,
      validator: (value, model) => {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(value, "text/html");
        const $imgNoAlt = $('img:not([alt]), img[alt=""]', htmlDoc);
        const $imgCaption = $imgNoAlt.closest('.fr-img-caption');

        return $imgNoAlt.length === 0 ||
               ($imgCaption.length !== 0 && $imgCaption.text().trim() !== ''
               );
      },
      type: ErrorType.accessibleImagesWarning,
      errorLevel: ValidationErrorLevel.Warning,
    }));
  }

  hasValidatorFor(key: string): boolean {
    return !!this._validators.find(validator => validator.validatorKey === key);
  }

  protected buildQuery(validatorKey, value?): {} {
    const q = super.buildQuery(validatorKey, value);
    q["formElementId"] = value?.client_id;
    if (validatorKey === "validation_rule_conditional_rule_sets" || validatorKey === "lookup_validation_rule_conditional_rule_sets" || validatorKey === "label_title") {
      q["formElementId"] = this.model.client_id;
    }
    return q
  }

}
